/**
 * NVE's grid time series API (gts.nve.no): the seNorge 1 km grids — snow depth, SWE, and
 * hourly temperature, precipitation and wind (MET Nordic analysis, then forecast ~2.5 days
 * ahead). Reached through our own proxy (`/api/gts`, see deploy/nginx.conf and vite.config.ts)
 * because the API sends no CORS headers.
 *
 * The grid is EPSG:25833 (UTM 33), 1195 columns from E −75 000, rows down from N 8 000 000.
 * Daily values are stamped 06:00 UTC and cover the 24 h ending then; hourly values are UTC.
 */

export const GTS_BASE = '/api/gts';
const GTS_COLS = 1195;
const GTS_MIN_E = -75_000;
const GTS_MAX_N = 8_000_000;
const GTS_CELL = 1000;

export function gtsCellIndex(e: number, n: number): number {
  const col = Math.floor((e - GTS_MIN_E) / GTS_CELL);
  const row = Math.floor((GTS_MAX_N - n) / GTS_CELL);
  return row * GTS_COLS + col;
}

export function gtsCellCenter(index: number): { e: number; n: number } {
  const row = Math.floor(index / GTS_COLS);
  const col = index % GTS_COLS;
  return {
    e: GTS_MIN_E + GTS_CELL * col + GTS_CELL / 2,
    n: GTS_MAX_N - GTS_CELL * row - GTS_CELL / 2,
  };
}

/** Indices of every 1 km cell overlapping a UTM box. */
export function gtsCellsInBox(minE: number, minN: number, maxE: number, maxN: number): number[] {
  const c0 = Math.floor((minE - GTS_MIN_E) / GTS_CELL);
  const c1 = Math.floor((maxE - GTS_MIN_E) / GTS_CELL);
  const r0 = Math.floor((GTS_MAX_N - maxN) / GTS_CELL);
  const r1 = Math.floor((GTS_MAX_N - minN) / GTS_CELL);
  const out: number[] = [];
  for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) out.push(r * GTS_COLS + c);
  return out;
}

export interface GtsCellSeries {
  cellIndex: number;
  /** Mean elevation of the 1 km cell (m). */
  altitude: number;
  /** Values in time order; NaN where the API has no data. */
  data: number[];
}

/** "2025-02-15" (daily themes) or "2025-02-15T06" (hourly / 3-hourly themes), UTC. */
export function gtsDate(d: Date, hourly: boolean): string {
  const iso = d.toISOString();
  return hourly ? iso.slice(0, 13) : iso.slice(0, 10);
}

interface GtsAreaResponse {
  CellTimeSeries: { CellIndex: number; Altitude: number; Data: number[] }[];
  NoDataValue: number;
}

/**
 * One theme for a set of cells over [start, end] (inclusive). Hourly themes (`tm1h`, `rr1h`,
 * `windSpeed10m1h`, `windDirection10m1h`) take hour precision; daily ones (`sd`, `swe`) days.
 */
export async function gtsArea(
  theme: string,
  cells: readonly number[],
  start: Date,
  end: Date,
  hourly: boolean,
  signal?: AbortSignal,
): Promise<GtsCellSeries[]> {
  const res = await fetch(`${GTS_BASE}/AreaTimeSeries/ByCellIndexCsv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      Theme: theme,
      StartDate: gtsDate(start, hourly),
      EndDate: gtsDate(end, hourly),
      Format: 'json',
      CellIndexCsv: cells.join(','),
    }),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) throw new Error(`seNorge ${theme}: HTTP ${res.status}`);
  const json = (await res.json()) as GtsAreaResponse;
  // Themes differ (65535 for most, 255 for some); anything at or above it is missing.
  const noData = json.NoDataValue;
  return json.CellTimeSeries.map((c) => ({
    cellIndex: c.CellIndex,
    altitude: c.Altitude,
    data: c.Data.map((v) => (v >= noData || !Number.isFinite(v) ? NaN : v)),
  }));
}
